import { motion } from "framer-motion";
import { Target, Users, Award, TrendingUp } from "lucide-react";

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Precision Engineering",
      desc: "Every product is engineered with meticulous attention to detail and quality.",
    },
    {
      icon: Users,
      title: "Customer-Centric",
      desc: "We prioritize customer satisfaction with tailored solutions and reliable support.",
    },
    {
      icon: Award,
      title: "Quality Assurance",
      desc: "Strict quality control standards ensure every stabilizer meets high performance benchmarks.",
    },
    {
      icon: TrendingUp,
      title: "Innovation Driven",
      desc: "Continuous R&D keeps us at the forefront of power conditioning technology.",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-main to-primary-dark text-white py-20" data-testid="about-hero-section">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 tracking-tight"
          >
            ABOUT VON
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg font-body max-w-3xl mx-auto opacity-90"
          >
            Delivering precision power solutions with decades of expertise
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white" data-testid="about-content-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1671351966453-3f1410780c41?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Manufacturing facility"
                className="rounded-sm shadow-lg w-full h-[400px] object-cover"
                data-testid="about-image"
              />
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-3xl font-heading font-bold text-primary-main mb-6">
                WHO WE ARE
              </h2>
              <div className="space-y-4 text-base text-secondary-main font-body leading-relaxed">
                <p>
                  VON specializes in manufacturing advanced Servo Voltage Stabilizers engineered for performance,
                  durability and precision.
                </p>
                <p>
                  Our products are built using high-grade components, modern manufacturing techniques and strict quality
                  control standards.
                </p>
                <p>
                  With a strong R&D team and modern production facilities, we deliver stabilizers that ensure stable
                  voltage, improved equipment safety and long-term reliability.
                </p>
                <p>
                  We serve residential, commercial and industrial customers across multiple sectors where voltage
                  stability is critical.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary-main mb-4">
              OUR CORE VALUES
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-sm p-6 text-center hover:shadow-md transition-shadow"
                  data-testid={`value-card-${index}`}
                >
                  <div className="bg-primary-main/10 w-16 h-16 rounded-sm flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-primary-main" size={32} />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-primary-main mb-3">
                    {value.title}
                  </h3>
                  <p className="text-sm text-secondary-main font-body">
                    {value.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-primary-main text-white" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-heading font-bold mb-6">OUR MISSION</h2>
            <p className="text-lg font-body leading-relaxed opacity-90">
              To provide cutting-edge voltage stabilization solutions that ensure uninterrupted power supply,
              safeguard valuable equipment, and enhance operational efficiency for our customers across all sectors.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
